import React, { useEffect, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { useAtom } from 'jotai';
import { powerAtom } from '../atoms-types/power';
import { CircularProgress } from '@mui/material';
import { BuildingIDProps } from '../atoms-types/chargers';
import moment, { Moment } from 'moment-timezone';
import { calculateTimeIntervals, formatTimeLabel, shouldShowTimeLabel } from '../converters/timespanConverter';

interface PowerGraphProps extends BuildingIDProps {
  startDateTime: Moment;
  endDateTime: Moment;
}

const PowerGraph: React.FC<PowerGraphProps> = ({ BuildingID, startDateTime, endDateTime }) => {
  const [powerDataMap] = useAtom(powerAtom);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 480);

  const powerData = BuildingID && Array.isArray(powerDataMap[BuildingID.toLowerCase()])? powerDataMap[BuildingID.toLowerCase()] : [];

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 480);
      const chartElement = document.querySelector('.echarts-for-react');
      if (chartElement) {
        const echartsInstance = (chartElement as any).__ECharts__;
        if (echartsInstance) {
          echartsInstance.resize();
        }
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize();
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Chart update effect - moved to top level
  useEffect(() => {
    if (!powerData || powerData.length === 0) return;
    
    const chartElement = document.querySelector('.echarts-for-react');
    if (chartElement) {
      const echartsInstance = (chartElement as any).__ECharts__;
      if (echartsInstance) {
        // Create data arrays for the update
        const Grid = powerData.map(b => b.Grid ?? 0);
        const Load = powerData.map(b => b.Load ?? 0);
        const Solar = powerData.map(b => b.Solar ?? 0);
        const Demand = powerData.map(b => b.Demand ?? 0);
        const DayPeak = powerData.map(b => b.DayPeak ?? 0);
        const NCharging = powerData.map(b => b.NCharging ?? 0);
        const NWaiting = powerData.map(b => b.NWaiting ?? 0);
        
        // Get current legend selection state to preserve it
        const currentOption = echartsInstance.getOption();
        const currentLegendSelected = currentOption.legend?.[0]?.selected || {};
        
        // Update the chart with new data while preserving legend selection
        const newOption = {
          series: [
            { data: Grid },
            { data: Load },
            { data: Solar },
            { data: Demand },
            { data: DayPeak },
            { data: NCharging },
            { data: NWaiting }
          ],
          legend: {
            selected: currentLegendSelected
          }
        };
        echartsInstance.setOption(newOption, true);
      }
    }
  }, [powerData]);

  if (!powerData) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        textAlign: 'center',
      }}>
        <CircularProgress style={{ color: 'firebrick' }} />
        <h3 style={{ color: 'text.primary', marginTop: '20px' }}>Waiting for data...</h3>
      </div>
    );
  }

  const xAxisData: string[] = [];
  const verticalLines: any[] = [];
  let previousDate = "";

  // Calculate time intervals based on timespan
  const { interval, showHalfHours } = calculateTimeIntervals(startDateTime, endDateTime);

  powerData.forEach((b, index) => {
    const timestamp = moment.utc(b.Timestamp).tz('America/New_York');
    const formattedTime = formatTimeLabel(timestamp, showHalfHours);
    const currentDate = timestamp.format('M/D');

    xAxisData.push(formattedTime);

    if (currentDate !== previousDate && timestamp.hour() === 0 && timestamp.minute() === 0) {
      verticalLines.push({
        xAxis: index,
        lineStyle: {
          color: 'lightgrey',
          type: 'dashed',
        },
        label: {
          formatter: currentDate,
          position: 'end',
          fontSize: 10,
          color: 'grey',
        },
        tooltip: {
          formatter: `Date: ${currentDate}`,
        },
      });
      previousDate = currentDate;
    }
  });

  const Grid: number[] = powerData.map(b => b.Grid ?? 0);
  const Load: number[] = powerData.map(b => b.Load ?? 0);
  const Solar: number[] = powerData.map(b => b.Solar ?? 0);
  const Demand: number[] = powerData.map(b => b.Demand ?? 0);
  const DayPeak: number[] = powerData.map(b => b.DayPeak ?? 0);
  const NCharging: number[] = powerData.map(b => b.NCharging ?? 0);
  const NWaiting: number[] = powerData.map(b => b.NWaiting ?? 0);

  const option = {
    backgroundColor: 'transparent',
    animation: true,
    animationDuration: 300,
    animationEasing: 'cubicOut',
    tooltip: {
      show: !isMobile,
      trigger: 'axis',
      axisPointer: {
        type: 'line',
      },
      formatter: function(params: any) {
        const timestamp = moment.utc(powerData[params[0].dataIndex].Timestamp).tz('America/New_York');
        const formattedTime = timestamp.format('MM/DD/YYYY h:mm:ss A');
        let result = `${formattedTime}<br/>`;
        
        params.forEach((param: any) => {
          const value = param.value === null || param.value === undefined ? 'No data' : Number(param.value).toFixed(4);
          const color = param.color;
          result += `<span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;background-color:${color};"></span>${param.seriesName}: ${value}<br/>`;
        });
        return result;
      }
    },
    legend: {
      data: ['Grid', 'Load', 'Solar', 'Demand', 'DayPeak', 'NCharging', 'NWaiting'],
      textStyle: {
        color: 'grey',
      },
      bottom: 0,
      padding: [5, 0, 0, 0],
      selected: {
        'Grid': true,
        'Load': true,
        'Solar': true,
        'Demand': true,
        'DayPeak': true,
        'NCharging': false,
        'NWaiting': false
      }
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '10%',
      containLabel: true,
    },
    toolbox: {
      feature: {
        saveAsImage: {},
      },
    },
    xAxis: {
      type: 'category',
      boundaryGap: true,
      data: xAxisData,
      axisLabel: {
        color: 'grey',
        rotate: 45,
        interval: (index: number) => {
          const timestamp = moment.utc(powerData[index].Timestamp).tz('America/New_York');
          return shouldShowTimeLabel(timestamp, startDateTime, interval, showHalfHours);
        },
        formatter: (value: string) => {
          const timestamp = moment.utc(powerData[xAxisData.indexOf(value)].Timestamp).tz('America/New_York');
          return formatTimeLabel(timestamp, showHalfHours);
        }
      },
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        color: 'grey',
      },
    },
    series: [
      {
        name: 'Grid',
        type: 'line',
        data: Grid,
        lineStyle: { color: 'firebrick' },
        itemStyle: { color: 'firebrick' },
        markLine: {
          symbol: 'none',
          data: verticalLines,
        },
      },
      {
        name: 'Load',
        type: 'line',
        data: Load,
        lineStyle: {
          color: 'steelblue'
        },
        itemStyle: {
          color: 'steelblue'
        }
      },
      {
        name: 'Solar',
        type: 'line',
        data: Solar,
        lineStyle: {
          color: 'orange'
        },
        itemStyle: {
          color: 'orange'
        }
      },
      {
        name: 'Demand',
        type: 'line',
        data: Demand,
        lineStyle: {
          color: 'slateblue'
        },
        itemStyle: {
          color: 'slateblue'
        }
      },
      {
        name: 'DayPeak',
        type: 'line',
        data: DayPeak,
        lineStyle: {
          color: 'seagreen'
        },
        itemStyle: {
          color: 'seagreen'
        }
      },
      {
        name: 'NCharging',
        type: 'line',
        data: NCharging,
        lineStyle: {
          color: 'purple'
        },
        itemStyle: {
          color: 'purple'
        }
      },
      {
        name: 'NWaiting',
        type: 'line',
        data: NWaiting,
        lineStyle: {
          color: 'lightsalmon'
        },
        itemStyle: {
          color: 'lightsalmon'
        }
      }
    ],
  };

  return(
    <div style={{ position: 'relative', height: '100%', width: '100%' }}>
      <ReactECharts 
        option={option} 
        className="cancelSelectorName" 
        style={{ height: '100%', width: '100%' }} 
        opts={{ renderer: 'canvas' }}
      />
      
    </div>
  )
};

export default PowerGraph;





